import React, { useRef, useState, useCallback } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Slider, Typography } from '@mui/material';
import ZoomIn from '@mui/icons-material/ZoomIn';

interface AvatarCropDialogProps {
  open: boolean;
  imageSrc: string | null;
  onClose: () => void;
  onCropComplete: (blob: Blob) => void;
}

// Square crop viewport - the export is a square image, displayed as a
// circle everywhere via CSS border-radius, so what's inside this container
// IS the final avatar (no visible/exported mismatch to reason about).
const VIEWPORT = 320;
const OUTPUT_SIZE = 400;

/**
 * A minimal, dependency-free avatar cropper: drag to reposition, slider to
 * zoom, confirm renders the visible window to an offscreen canvas. Built
 * in-house rather than pulling in a cropping library for one dialog.
 */
export const AvatarCropDialog: React.FC<AvatarCropDialogProps> = ({
  open,
  imageSrc,
  onClose,
  onCropComplete,
}) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragState = useRef<{ startX: number; startY: number; originX: number; originY: number } | null>(null);

  const baseScale = naturalSize.width && naturalSize.height
    ? VIEWPORT / Math.min(naturalSize.width, naturalSize.height)
    : 1;
  const displayScale = baseScale * zoom;
  const displayWidth = naturalSize.width * displayScale;
  const displayHeight = naturalSize.height * displayScale;

  const handleImageLoad = () => {
    const img = imgRef.current;
    if (!img) return;
    const width = img.naturalWidth;
    const height = img.naturalHeight;
    setNaturalSize({ width, height });
    const scale = VIEWPORT / Math.min(width, height);
    setOffset({ x: (VIEWPORT - width * scale) / 2, y: (VIEWPORT - height * scale) / 2 });
    setZoom(1);
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragState.current = { startX: e.clientX, startY: e.clientY, originX: offset.x, originY: offset.y };
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!dragState.current) return;
    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;
    setOffset({ x: dragState.current.originX + dx, y: dragState.current.originY + dy });
  };

  const handlePointerUp = () => {
    dragState.current = null;
  };

  const handleZoomChange = useCallback(
    (_: Event, value: number | number[]) => {
      const nextZoom = value as number;
      // Keep the viewport center fixed as zoom changes, instead of the
      // image jumping to re-center on its own top-left corner.
      const nextScale = baseScale * nextZoom;
      const cx = VIEWPORT / 2;
      const cy = VIEWPORT / 2;
      const imgX = (cx - offset.x) / displayScale;
      const imgY = (cy - offset.y) / displayScale;
      setOffset({ x: cx - imgX * nextScale, y: cy - imgY * nextScale });
      setZoom(nextZoom);
    },
    [baseScale, displayScale, offset]
  );

  const handleConfirm = () => {
    const img = imgRef.current;
    if (!img || !naturalSize.width) return;

    const sx = (0 - offset.x) / displayScale;
    const sy = (0 - offset.y) / displayScale;
    const sSize = VIEWPORT / displayScale;

    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(img, sx, sy, sSize, sSize, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);

    canvas.toBlob(
      (blob) => {
        if (blob) onCropComplete(blob);
      },
      'image/jpeg',
      0.92
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Adjust your photo</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            width: VIEWPORT,
            height: VIEWPORT,
            mx: 'auto',
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 2,
            bgcolor: '#0f172a',
            cursor: dragState.current ? 'grabbing' : 'grab',
            touchAction: 'none',
          }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
        >
          {imageSrc && (
            <img
              ref={imgRef}
              src={imageSrc}
              alt="Crop preview"
              onLoad={handleImageLoad}
              draggable={false}
              style={{
                position: 'absolute',
                left: offset.x,
                top: offset.y,
                width: displayWidth || undefined,
                height: displayHeight || undefined,
                userSelect: 'none',
                pointerEvents: 'none',
              }}
            />
          )}
          {/* Circular guide - what's inside this ring is the exported square */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              boxShadow: '0 0 0 2000px rgba(15, 23, 42, 0.55)',
              border: '2px solid rgba(255,255,255,0.9)',
              pointerEvents: 'none',
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 3, px: 1 }}>
          <ZoomIn fontSize="small" sx={{ color: 'text.secondary' }} />
          <Slider
            value={zoom}
            min={1}
            max={3}
            step={0.01}
            onChange={handleZoomChange}
            aria-label="Zoom"
          />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
          Drag to reposition · slider to zoom
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={!naturalSize.width}>
          Save Photo
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AvatarCropDialog;
